import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api, formatINR, timeAgo, blurOnWheel } from "../lib/api";
import { Check, X, Flame, AlertTriangle, Split, FileText, ExternalLink, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";

export default function ApprovalFeed() {
  const { user } = useAuth();
  // v2.11 — b2 read-only path: users with view_payment_queue (but not
  // approve_requests) reach this page and see the pending items without
  // Approve/Reject buttons.
  const canApprove = user?.role === "admin" || !!user?.permissions_effective?.approve_requests;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortDesc, setSortDesc] = useState(null);  // v2.12 — null = newest first (default)
  const [confirmAction, setConfirmAction] = useState(null); // { type, request, approvedAmount, reason }
  // Phase F2 — duplicate-confirm dialog state.
  const [dupConfirm, setDupConfirm] = useState(null); // { request, approvedAmount, matches }
  const navigate = useNavigate();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await api.get("/requests?status=pending");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const filtered = items
    .filter((r) => {
      if (filter === "high") return r.amount >= 50000;
      return true;
    })
    // v2.12 — default sort: newest first by created_at. Amount sort toggle
    // still available via the sortDesc/asc control if the user wants it.
    .sort((a, b) => {
      if (sortDesc == null) {
        return (b.created_at || "").localeCompare(a.created_at || "");
      }
      return sortDesc ? b.amount - a.amount : a.amount - b.amount;
    });

  const doAction = async () => {
    if (!confirmAction) return;
    const { type, request, approvedAmount, reason } = confirmAction;
    try {
      if (type === "approve") {
        await api.patch(`/requests/${request.request_id}/approve`, {
          approved_amount: approvedAmount != null ? Number(approvedAmount) : null,
        });
        toast.success("Approved");
      } else {
        await api.patch(`/requests/${request.request_id}/reject`, { reason: reason || null });
        toast.success("Rejected");
      }
      setConfirmAction(null);
      fetchItems();
    } catch (e) {
      // Phase F2 — backend returns 409 when duplicates are found; surface a
      // separate confirm dialog showing the prior request(s).
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "possible_duplicate") {
        setDupConfirm({
          request,
          approvedAmount,
          matches: detail.matches || [],
        });
        setConfirmAction(null);
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Action failed"));
    }
  };

  // Approve-anyway → re-fire with duplicate_ack: true. Audit-logged server-side.
  const approveAnyway = async () => {
    if (!dupConfirm) return;
    const { request, approvedAmount, matches } = dupConfirm;
    try {
      await api.patch(`/requests/${request.request_id}/approve`, {
        approved_amount: approvedAmount != null ? Number(approvedAmount) : null,
        duplicate_ack: true,
        duplicate_of: matches.map((m) => m.request_id),
      });
      toast.success("Approved (duplicate warning acknowledged)");
      setDupConfirm(null);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <AppShell back="/dashboard" title="APPROVALS">
      <div className="px-5 pt-5 pb-3 space-y-3">
        <div className="flex justify-between items-end">
          <h2 className="font-semibold text-2xl tracking-tight uppercase">
            {items.length} <span className="text-slate-400">PENDING</span>
          </h2>
          <button
            data-testid="sort-toggle"
            onClick={() => setSortDesc(!sortDesc)}
            className="font-mono text-[10px] uppercase tracking-widest font-bold border border-black px-2 py-1 hover:bg-black hover:text-white"
          >
            {sortDesc ? "₹ HIGH→LOW" : "₹ LOW→HIGH"}
          </button>
        </div>

        <div className="flex gap-0 border-t border-l border-slate-200">
          {["all", "high", "emergency"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`flex-1 py-2 border-r border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest font-bold transition-colors ${
                filter === f ? "bg-black text-white" : "bg-white text-black hover:bg-slate-50"
              }`}
            >
              {f === "all" ? "ALL" : f === "high" ? "HIGH VALUE" : "EMERGENCY"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-0 lg:grid lg:grid-cols-2 lg:gap-0 lg:border-t lg:border-l lg:border-slate-200 lg:space-y-0">
        {loading && <div className="p-5 label-micro">LOADING…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-400 lg:col-span-2">
            No pending requests
          </div>
        )}
        {filtered.map((r) => (
          <ApprovalCard
            key={r.request_id}
            r={r}
            canApprove={canApprove}
            onApprove={() => setConfirmAction({ type: "approve", request: r, approvedAmount: r.amount })}
            onReject={() => setConfirmAction({ type: "reject", request: r, reason: "" })}
            onClick={() => navigate(`/requests/${r.request_id}`)}
          />
        ))}
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent className="max-w-md  border border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-semibold uppercase tracking-tight">
              {confirmAction?.type === "approve" ? "Approve Request" : "Reject Request"}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-xs">
              {confirmAction?.request && (
                <>
                  <span className="font-bold">{confirmAction.request.request_id}</span> — {confirmAction.request.vendor_name}
                  <br/>
                  Requested: <span className="font-bold">{formatINR(confirmAction.request.amount)}</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirmAction?.type === "approve" && (
            <div className="space-y-2">
              <div className="label-micro">APPROVED AMOUNT</div>
              <Input
                data-testid="approve-amount"
                type="number"
                onWheel={blurOnWheel}
                onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                value={confirmAction.approvedAmount ?? ""}
                onChange={(e) => setConfirmAction({ ...confirmAction, approvedAmount: e.target.value })}
                className=" border border-slate-200 font-display text-xl font-black"
              />
            </div>
          )}
          {confirmAction?.type === "reject" && (
            <div className="space-y-2">
              <div className="label-micro">REASON (OPTIONAL)</div>
              <Input
                data-testid="reject-reason"
                value={confirmAction.reason ?? ""}
                onChange={(e) => setConfirmAction({ ...confirmAction, reason: e.target.value })}
                className=" border border-slate-200 font-mono"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-action" className=" border-black">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-action"
              onClick={doAction}
              className={` ${confirmAction?.type === "approve" ? "bg-black hover:bg-olive" : "bg-slate-800 hover:bg-slate-900"}`}
            >
              {confirmAction?.type === "approve" ? "APPROVE" : "REJECT"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase F2 — duplicate confirmation dialog */}
      <AlertDialog open={!!dupConfirm} onOpenChange={(o) => !o && setDupConfirm(null)}>
        <AlertDialogContent
          className="max-w-lg border border-amber-300 rounded-2xl"
          data-testid="duplicate-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-semibold tracking-tight flex items-center gap-2 text-amber-900">
              <AlertTriangle size={18} className="text-amber-600"/>
              Possible duplicate detected
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-slate-600">
              This request matches{" "}
              <span className="font-semibold">
                {dupConfirm?.matches?.length === 1 ? "1 prior request" : `${dupConfirm?.matches?.length || 0} prior requests`}
              </span>
              {" "}on the same vendor, category, and amount (within 5%) in the last 7 days.
              You can review them below and approve anyway — the action will be audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="duplicate-confirm-list">
            {(dupConfirm?.matches || []).slice(0, 6).map((m, idx) => (
              <DuplicateConfirmRow key={m.request_id} match={m} highlight={idx === 0} />
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="duplicate-cancel"
              className="border-slate-300"
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="duplicate-approve-anyway"
              onClick={approveAnyway}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Approve anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function ApprovalCard({ r, canApprove, onApprove, onReject, onClick }) {
  const isEmergency = r.urgency === "emergency";
  const isHigh = r.amount >= 50000;
  const flags = r.flags || {};
  const reqMode = r.requested_payment_mode;
  const supportCount = (r.supporting_docs || []).length;

  return (
    <div
      data-testid={`approval-card-${r.request_id}`}
      className={`bg-white border-b border-slate-200/10 relative ${isEmergency ? "border-l-[6px] border-l-amber-500" : isHigh ? "border-l-[6px] border-l-olive" : ""}`}
    >
      <button onClick={onClick} className="block w-full text-left px-5 pt-4 pb-3">
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="font-semibold text-sm uppercase tracking-tight">{r.vendor_name}</div>
            <div className="label-micro mt-0.5">{r.site} · {r.category}</div>
          </div>
          <div className="label-micro">{timeAgo(r.created_at)}</div>
        </div>
        <div className="flex items-end justify-between">
          <div className="font-semibold text-4xl tracking-tight">{formatINR(r.amount)}</div>
          <div className="flex gap-1 flex-wrap justify-end">
            {isEmergency && <Tag color="amber" icon={Flame}>EMERGENCY</Tag>}
            {flags.post_facto && <Tag color="red"><AlertTriangle size={10}/>POST-FACTO</Tag>}
            {flags.split && <Tag color="red"><Split size={10}/>SPLIT</Tag>}
            {flags.sla_breach && <Tag color="red"><AlertTriangle size={10}/>SLA</Tag>}
          </div>
        </div>
        {/* Requested payment mode + supporting docs — at-a-glance facts for the approver */}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] font-mono">
          {reqMode ? (
            <span data-testid={`req-mode-${r.request_id}`} className="inline-flex items-center gap-1.5 bg-olive/10 text-olive border border-olive/40 px-2 py-0.5 uppercase tracking-widest font-bold">
              REQUESTED MODE · {reqMode}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 border border-slate-300 px-2 py-0.5 uppercase tracking-widest">
              MODE NOT SET
            </span>
          )}
          {supportCount > 0 && (
            <span data-testid={`docs-count-${r.request_id}`} className="inline-flex items-center gap-1.5 bg-white text-black border border-black px-2 py-0.5 uppercase tracking-widest font-bold">
              <FileText size={11}/> {supportCount} DOC{supportCount === 1 ? "" : "S"}
            </span>
          )}
        </div>
        {r.notes && <div className="mt-2 font-mono text-xs text-slate-600 italic">"{r.notes}"</div>}
        <div className="mt-2 font-mono text-[10px] text-slate-400">BY {r.created_by_name} · {r.request_id}</div>
      </button>
      <div className={`${canApprove ? "grid grid-cols-2" : "flex"} border-t border-slate-200`}>
        {canApprove ? (
          <>
            <button
              data-testid={`reject-${r.request_id}`}
              onClick={(e) => { e.stopPropagation(); onReject(); }}
              className="py-3 border-r border-slate-200 font-mono text-xs uppercase tracking-widest font-bold hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-center gap-1"
            >
              <X size={14}/> REJECT
            </button>
            <button
              data-testid={`approve-${r.request_id}`}
              onClick={(e) => { e.stopPropagation(); onApprove(); }}
              className="py-3 font-mono text-xs uppercase tracking-widest font-bold bg-black text-white hover:bg-olive transition-colors flex items-center justify-center gap-1"
            >
              <Check size={14}/> APPROVE
            </button>
          </>
        ) : (
          <div
            data-testid={`view-only-${r.request_id}`}
            className="w-full py-3 font-mono text-[11px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50 flex items-center justify-center gap-2"
          >
            <Eye size={12}/> VIEW ONLY · APPROVE PERMISSION NOT GRANTED
          </div>
        )}
      </div>
    </div>
  );
}

const Tag = ({ color, icon: Icon, children }) => {
  const cls = color === "red"
    ? "bg-red-600 text-white"
    : color === "amber"
    ? "bg-amber-500 text-black"
    : color === "yellow"
    ? "bg-yellow-400 text-black"
    : "bg-slate-200 text-black";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest font-bold ${cls}`}>
      {Icon && <Icon size={10}/>} {children}
    </span>
  );
};


// Phase F2 — match row inside the duplicate-confirm dialog.
function DuplicateConfirmRow({ match, highlight }) {
  const days = (() => {
    try {
      const d = new Date(match.created_at);
      const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (diff <= 0) return "today";
      if (diff === 1) return "1 day ago";
      return `${diff} days ago`;
    } catch { return ""; }
  })();
  const amt = Math.round(match.approved_amount ?? match.amount ?? 0);
  return (
    <div
      data-testid={`duplicate-confirm-row-${match.request_id}`}
      className={`rounded-xl border px-3 py-2.5 transition ${
        highlight ? "border-amber-400 bg-amber-50 ring-1 ring-amber-200" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 truncate">{match.request_id}</span>
            <span className="font-semibold text-sm text-olive">₹{amt.toLocaleString("en-IN")}</span>
            {highlight && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                Strongest match
              </span>
            )}
            {match.same_site && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-olive/10 text-olive px-1.5 py-0.5 rounded">
                Same site
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-0.5 truncate">
            {match.vendor_name || "—"} · {match.category} · {match.site} · {match.status} {days ? `· ${days}` : ""}
          </div>
        </div>
        <a
          href={`/requests/${match.request_id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`duplicate-confirm-view-${match.request_id}`}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50"
          onClick={(e) => e.stopPropagation()}
        >
          View <ExternalLink size={12}/>
        </a>
      </div>
    </div>
  );
}
